package org.moxultimate.topwithmox.components.buttons

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.selectors.active
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.px
import org.moxultimate.topwithmox.components.texts.DescriptionText
import org.moxultimate.topwithmox.theme.FontFamily
import org.moxultimate.topwithmox.theme.WhiteTextColor

/**
 * GradientButton
 * Orange-to-purple gradient pill button matching the btn_gradient.webp design.
 * Pure CSS — no image file, crisp at every resolution.
 *
 * @param text Label rendered on the button.
 * @param buttonWidth Fixed width in px. Pass 0 (default) to auto-size based on text length.
 * @param buttonHeight Height in px. Defaults to 60.
 * @param fontSize Custom font size in px. Pass 0 (default) to use the standard DescriptionText clamp.
 * @param horizontalPadding Left/right padding in px when buttonWidth is 0. Defaults to 32.
 * @param disabled When true renders DisabledButton instead.
 * @param modifier Optional outer modifier (e.g. margin, alignment).
 * @param onClick Click callback; not invoked when disabled.
 */
@Composable
fun GradientButton(
    text: String,
    buttonWidth: Int = 0,
    buttonHeight: Int = 60,
    fontSize: Int = 0,
    horizontalPadding: Int = 32,
    disabled: Boolean = false,
    modifier: Modifier = Modifier,
    onClick: () -> Unit,
) {
    if (disabled) {
        DisabledButton(text = text, buttonWidth = buttonWidth, modifier = modifier)
    } else {
        Box(
            modifier = modifier
                .then(PressableButtonStyle.toModifier())
                .then(if (buttonWidth > 0) Modifier.width(buttonWidth.px) else Modifier.padding(leftRight = horizontalPadding.px))
                .height(buttonHeight.px)
                .borderRadius(30.px)
                .styleModifier {
                    property("background", "linear-gradient(to right, #FF7034, #F03A85, #8B5CF6)")
                    property("box-shadow", "0px 4px 15px rgba(0, 0, 0, 0.22)")
                }
                .onClick { onClick() },
            contentAlignment = Alignment.Center,
        ) {
            if (fontSize > 0) {
                SpanText(
                    text = text,
                    modifier = Modifier
                        .color(WhiteTextColor)
                        .fontFamily(FontFamily)
                        .fontSize(fontSize.px)
                        .fontWeight(FontWeight.Normal),
                )
            } else {
                DescriptionText(description = text)
            }
        }
    }
}

/**
 * DisabledButton
 * Gray pill button matching the btn_disabled.webp design.
 * Used when an action is unavailable — muted gray pill, no hover or click effects.
 *
 * @param text Label rendered on the button.
 * @param buttonWidth Fixed width in px. Pass 0 (default) to auto-size based on text length.
 * @param modifier Optional outer modifier.
 */
@Composable
fun DisabledButton(
    text: String,
    buttonWidth: Int = 0,
    modifier: Modifier = Modifier,
) {
    Box(
        modifier = modifier
            .then(if (buttonWidth > 0) Modifier.width(buttonWidth.px) else Modifier.padding(leftRight = 32.px))
            .height(60.px)
            .borderRadius(30.px)
            .styleModifier {
                property("background", "#B0B0B0")
                property("box-shadow", "0px 4px 12px rgba(0, 0, 0, 0.14)")
                property("cursor", "not-allowed")
            },
        contentAlignment = Alignment.Center,
    ) {
        DescriptionText(description = text)
    }
}

/**
 * CardHoverStyle
 * Subtle lift animation for content cards — scales up slightly on hover, sinks on press.
 * Apply to the outermost container of a card to give it interactive depth.
 */
val CardHoverStyle = CssStyle {
    base {
        Modifier.styleModifier {
            property("transition", "transform 0.2s ease, box-shadow 0.2s ease")
        }
    }
    hover {
        Modifier.styleModifier {
            property("transform", "scale(1.03)")
            property("box-shadow", "0 8px 32px rgba(0,0,0,0.13)")
        }
    }
    active {
        Modifier.styleModifier {
            property("transform", "scale(0.98)")
        }
    }
}

/**
 * PressableButtonStyle
 * Press animation — lifts on hover, sinks on active click.
 */
val PressableButtonStyle = CssStyle {
    base {
        Modifier.styleModifier {
            property("cursor", "pointer")
            property("transition", "transform 0.15s ease, opacity 0.15s ease")
        }
    }
    hover {
        Modifier.styleModifier {
            property("transform", "scale(1.04)")
        }
    }
    active {
        Modifier.styleModifier {
            property("transform", "scale(0.93)")
            property("opacity", "0.85")
        }
    }
}
